import { getPagesApi } from '@/api-calls/posts/get'
import { getSiteApi, getSitesApi } from '@/api-calls/sites/get'
import { deleteSiteApi, updateSiteApi } from '@/api-calls/sites/mutate'
import { ContainerWithSidebarAndNoTopbar } from '@/components/header/container'
import { updateSiteAdvancedSchema } from '@/lib/schema/sites'
import { SectionHeading } from './section-heading'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
} from '@blogging/ui'
import { useFetch, useMutation, useQueryClient } from '@ngtrvu/use-api'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

export function SiteSettingsAdvancedPage() {
  const { siteId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState('')

  const {
    data: site,
    isLoading,
    error,
  } = useFetch(getSiteApi, {
    resourceName: 'site',
    initialParams: { id: siteId as string },
  })

  // Fetch pages for the pageId selector
  const { data: pagesData } = useFetch(getPagesApi, {
    resourceName: 'pages',
    initialParams: { siteId: siteId as string },
  })

  const { mutate: updateSite, isPending: isUpdating } = useMutation(
    updateSiteApi,
    {
      resourceName: 'site',
      onSuccess: (data: any) => {
        // Seed the cache with the server's own response instead of waiting on
        // a background refetch — avoids a window where an in-flight stale
        // fetch could resolve after invalidation and clobber the fresh value.
        // Merge onto the existing cached site rather than replacing it: the
        // PATCH response doesn't include `userRole` (only the GET response
        // does), and this same cache entry backs the AdminGuard's isAdmin
        // check, so overwriting it wholesale would drop the role and lock
        // the user out of the settings pages.
        queryClient.setQueryData(
          [getSiteApi.apiName, { id: siteId }],
          (old: any) => ({
            ...old,
            site: { ...old?.site, ...data?.site },
          }),
        )
        toast.success('Advanced settings updated successfully!')
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to update advanced settings')
      },
    },
  )

  const { mutate: deleteSite, isPending: isDeleting } = useMutation(
    deleteSiteApi,
    {
      resourceName: 'site',
      onSuccess: () => {
        toast.success('Site deleted successfully')
        setIsDeleteDialogOpen(false)
        queryClient.invalidateQueries({
          queryKey: [getSitesApi.apiName],
        })
        navigate('/')
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to delete site')
      },
    },
  )

  const form = useForm({
    resolver: zodResolver(updateSiteAdvancedSchema),
    values: site
      ? {
          copyrightText: site.copyrightText ?? '',
          blogLayout: (site.blogLayout as 'list' | 'grid') ?? 'grid',
          blogShowFeatureImage: site.blogShowFeatureImage ?? true,
          pageLayout: (site.pageLayout as 'blog' | 'page' | 'pagex') ?? 'blog',
          pageId: site.pageId ?? null,
          appName: site.appName ?? '',
          appUrl: site.appUrl ?? '',
          searchIndexingEnabled: site.searchIndexingEnabled ?? false,
        }
      : undefined,
    defaultValues: {
      copyrightText: '',
      blogLayout: 'grid' as 'list' | 'grid',
      blogShowFeatureImage: true,
      pageLayout: 'blog' as 'blog' | 'page' | 'pagex',
      pageId: null as string | null,
      appName: '',
      appUrl: '',
      searchIndexingEnabled: false,
    },
  })

  // Watch pageLayout to conditionally show pageId selector
  // Use watched value, but fall back to site data for initial render
  const watchedPageLayout = form.watch('pageLayout')
  const pageLayout = watchedPageLayout || site?.pageLayout || 'blog'

  // A verified primary custom domain proves domain ownership, so indexing
  // takes effect immediately instead of going through admin review.
  const hasVerifiedCustomDomain = !!(site?.domain && site?.domainVerified)

  const handleDeleteSite = () => {
    if (!site || deleteConfirmation !== site.name) {
      toast.error('Please enter the site name correctly to confirm deletion')
      return
    }
    deleteSite({ id: siteId as string, confirmation: deleteConfirmation })
  }

  const onSubmit = async (data: any) => {
    try {
      updateSite({
        id: siteId as string,
        ...data,
      })
    } catch (error) {
      console.error('Failed to update site:', error)
    }
  }

  if (isLoading) {
    return (
      <ContainerWithSidebarAndNoTopbar>
        <Skeleton className="h-48 w-full rounded-lg" />
      </ContainerWithSidebarAndNoTopbar>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center">
        <p className="text-destructive">Error loading site settings</p>
        <Button variant="outline" onClick={() => navigate(0)} className="mt-2">
          Retry
        </Button>
      </div>
    )
  }

  return (
    <ContainerWithSidebarAndNoTopbar>
      <div className="flex flex-col space-y-6">
        <div className="flex flex-col space-y-1">
          <h1 className="text-2xl font-bold">Advanced Settings</h1>
          <p className="text-sm text-muted-foreground">
            Configure advanced options for your site.
          </p>
        </div>

        <div className="rounded-lg border bg-card p-5">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="divide-y">
              {/* Content & Layout */}
              <div className="space-y-4 pb-5">
                <SectionHeading>Content & Layout</SectionHeading>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="pageLayout"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Homepage Layout</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || site?.pageLayout || 'blog'}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select layout" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="blog">
                              Blog - Latest posts
                            </SelectItem>
                            <SelectItem value="page">
                              Page - A specific page
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="blogLayout"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Blog Layout</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select layout" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="grid">Grid</SelectItem>
                            <SelectItem value="list">List</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {pageLayout === 'page' && (
                  <FormField
                    control={form.control}
                    name="pageId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Homepage Page</FormLabel>
                        {pagesData && pagesData.length > 0 ? (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || site?.pageId || undefined}
                          >
                            <FormControl>
                              <SelectTrigger className="max-w-md">
                                <SelectValue placeholder="Select a page" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {pagesData.map(
                                (page: { id: string; title: string }) => (
                                  <SelectItem key={page.id} value={page.id}>
                                    {page.title}
                                  </SelectItem>
                                ),
                              )}
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            No pages available. Create a page first to use this
                            layout.
                          </p>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="copyrightText"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Copyright Text</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={`© ${new Date().getFullYear()} ${site?.name}. All rights reserved.`}
                          {...field}
                          className="max-w-md"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="blogShowFeatureImage"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between gap-4 space-y-0">
                      <div className="space-y-0.5">
                        <FormLabel className="text-sm font-medium">
                          Show Feature Image
                        </FormLabel>
                        <FormDescription className="text-xs">
                          Display featured images on blog post cards.
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              {/* Search Engine Indexing */}
              <div className="space-y-3 py-5">
                <SectionHeading>Search Engine Indexing</SectionHeading>

                <FormField
                  control={form.control}
                  name="searchIndexingEnabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between gap-4 space-y-0">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <FormLabel className="text-sm font-medium">
                            Allow search engines to index this site
                          </FormLabel>
                          <SearchIndexingStatusBadge
                            enabled={field.value}
                            status={site?.searchIndexingStatus}
                          />
                        </div>
                        <FormDescription className="text-xs">
                          {hasVerifiedCustomDomain &&
                          site?.searchIndexingStatus !== 'rejected'
                            ? "Your custom domain is verified, so this takes effect immediately — no admin review needed."
                            : 'Submits your site for admin review — it only becomes visible to search engines once approved.'}
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {form.watch('searchIndexingEnabled') &&
                  site?.searchIndexingStatus !== 'approved' && (
                    <p className="text-xs text-muted-foreground">
                      All sites are subject to our content policy — spam,
                      scam, and cryptocurrency-promotion sites are not
                      eligible and may be delisted at any time. See our{' '}
                      <a
                        href="https://blogging.dev/terms"
                        target="_blank"
                        rel="noreferrer"
                        className="underline underline-offset-2 hover:text-foreground"
                      >
                        Terms of Service
                      </a>{' '}
                      for details.
                    </p>
                  )}
              </div>

              {/* App */}
              <div className="space-y-4 py-5">
                <SectionHeading>App</SectionHeading>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="appName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>App Name</FormLabel>
                        <FormControl>
                          <Input placeholder="My Awesome App" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="appUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>App URL</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://my-awesome.app"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex flex-col items-start gap-3 pt-5 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  Make sure to save your changes before leaving this page.
                </p>
                <Button
                  type="submit"
                  disabled={isUpdating || !form.formState.isDirty}
                  className="w-full sm:w-auto"
                >
                  {isUpdating && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </Form>
        </div>

        {/* Danger Zone */}
        <div className="rounded-lg border border-destructive/50 bg-card p-5">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <h3 className="text-sm font-semibold text-destructive">
                Danger Zone
              </h3>
            </div>

            <div className="flex flex-col items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium">Delete this site</p>
                <p className="text-sm text-muted-foreground">
                  Once you delete a site, there is no going back. All posts,
                  pages, and settings will be permanently removed.
                </p>
              </div>
              <Dialog
                open={isDeleteDialogOpen}
                onOpenChange={(open) => {
                  setIsDeleteDialogOpen(open)
                  if (!open) setDeleteConfirmation('')
                }}
              >
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="w-full sm:w-auto"
                >
                  Delete Site
                </Button>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-destructive" />
                      Delete Site
                    </DialogTitle>
                    <DialogDescription>
                      This action cannot be undone. This will permanently delete
                      your site and remove all associated data including posts,
                      pages, and custom domains.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="rounded-lg bg-destructive/10 p-3">
                      <p className="text-sm text-destructive">
                        To confirm, type{' '}
                        <span className="font-mono font-bold">
                          {site?.name}
                        </span>{' '}
                        below:
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmation">Site name</Label>
                      <Input
                        id="confirmation"
                        placeholder="Enter site name to confirm"
                        value={deleteConfirmation}
                        onChange={(e) => setDeleteConfirmation(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsDeleteDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={handleDeleteSite}
                      disabled={isDeleting || deleteConfirmation !== site?.name}
                    >
                      {isDeleting && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      {isDeleting ? 'Deleting...' : 'Delete Site'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>
    </ContainerWithSidebarAndNoTopbar>
  )
}

function SearchIndexingStatusBadge({
  enabled,
  status,
}: {
  enabled?: boolean
  status?: string
}) {
  if (!enabled) {
    return <Badge variant="outline">Not requested</Badge>
  }
  if (status === 'approved') {
    return <Badge variant="default">Approved</Badge>
  }
  if (status === 'rejected') {
    return <Badge variant="destructive">Rejected</Badge>
  }
  return <Badge variant="secondary">Pending review</Badge>
}
